/**
 * Home, vedený start po přihlášení. Valuace v hlavní roli + jak to funguje + tarify.
 */
import React, { useEffect, useState } from 'react';
import { listValuations } from '../lib/apiFetch';
import { Icon } from './PortalLayout';

const ACCENT = 'var(--purple-glow)';

function fmtCZK(v) {
  if (v === null || v === undefined) return '-';
  return `${Math.round(v).toLocaleString('cs-CZ')} Kč`;
}
function fmtDate(s) {
  if (!s) return '';
  try { return new Date(s).toLocaleDateString('cs-CZ', { day: 'numeric', month: 'numeric', year: 'numeric' }); }
  catch { return ''; }
}
function plural(n) {
  if (n === 1) return 'uložená valuace';
  if (n >= 2 && n <= 4) return 'uložené valuace';
  return 'uložených valuací';
}

const STEPS = [
  ['1', 'Zadáte čísla', 'Tržby, náklady a pár údajů o byznysu. Nevíte, kde je najít? Poradí vám AI Poradce přímo ve formuláři.'],
  ['2', 'Spočítáme hodnotu', 'Tržní valuace násobkem provozního zisku, jak firmy oceňují investoři a kupci.'],
  ['3', 'Dostanete výsledek', 'Hodnotu vaší firmy + co ji zvyšuje a snižuje. V PRO i expertní report a pohled investora.'],
];

// Náhled placených tarifů (KOMPLET doporučený). FREE je zmíněn pod kartami.
const TARIFFS = [
  { key: 'pro', name: 'PRO valuace', price: '6 990 Kč', desc: 'Proč má firma takovou hodnotu a jak ji zvýšit.' },
  { key: 'audit', name: 'Audit podniku', price: '4 990 Kč', desc: 'Kde ztrácíte zisk a co s tím udělat.' },
  { key: 'komplet', name: 'Kompletní řešení', price: '9 990 Kč', desc: 'PRO + Audit dohromady. Ušetříte 1 990 Kč.', rec: true },
];

export function Home({ isStaff, isAdmin, userName, onNavigate, onNew, onOpen }) {
  const [count, setCount] = useState(null);
  const [recent, setRecent] = useState(null);

  useEffect(() => {
    listValuations().then(d => {
      const list = d.valuations || [];
      setCount(list.length);
      setRecent(list[0] || null);
    }).catch(() => { setCount(null); setRecent(null); });
  }, []);

  const secondary = [
    { key: 'dashboard', icon: 'dashboard', title: 'Ocenění a audit',
      desc: count === null ? 'Vaše ocenění a audity.' : `${count} ${plural(count)}.`, action: () => onNavigate('dashboard') },
    { key: 'billing', icon: 'billing', title: 'Tarify', desc: 'Co je ve FREE, PRO a Auditu.', action: () => onNavigate('billing') },
    { key: 'consult', icon: 'consult', title: 'Konzultace', desc: 'Napište našemu týmu / AI poradci.', action: () => onNavigate('consult') },
    { key: 'account', icon: 'account', title: 'Můj účet', desc: 'Profil a heslo.', action: () => onNavigate('account') },
    ...(isStaff ? [{ key: 'users', icon: 'users', title: 'Uživatelé', desc: 'Správa účtů a klientů.', action: () => onNavigate('users') }] : []),
    ...(isAdmin ? [{ key: 'crawler', icon: 'crawler', title: 'Crawler', desc: 'Interní nástroj, otevře se v nové záložce.', action: () => onNavigate('crawler') }] : []),
  ];

  return (
    <div style={{ maxWidth: 980, margin: '0 auto', padding: '2rem 1rem', fontFamily: "'Inter', sans-serif" }}>

      {/* HERO, valuace v hlavní roli */}
      <div style={hero}>
        <div style={{ fontSize: '0.78rem', color: ACCENT, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: '0.5rem' }}>
          Vítejte{userName ? `, ${userName}` : ''} · Valuace e-commerce
        </div>
        <h1 style={{ margin: '0 0 0.5rem', fontFamily: "'Source Serif 4', Georgia, serif", color: 'var(--star-white)', fontSize: '2rem', lineHeight: 1.15 }}>
          Kolik stojí váš podnik?
        </h1>
        <p style={{ color: 'var(--text-dim)', fontSize: '1rem', lineHeight: 1.55, maxWidth: 580, margin: '0 0 1.25rem' }}>
          Z jednoho vyplnění spočítáme <strong>tržní hodnotu vaší firmy</strong> a můžeme rovnou udělat i{' '}
          <strong>audit, kde ztrácíte zisk</strong>. Uvidíte, kolik firma stojí a jak hodnotu zvýšit.
        </p>
        <button onClick={onNew} style={heroCta}>Spustit analýzu →</button>
        <div style={{ fontSize: '0.82rem', color: 'var(--text-dim)', marginTop: '0.6rem' }}>
          Základní ocenění je zdarma, audit i PRO si pak vyberete na konci. Žádná platba předem.
        </div>
      </div>

      {/* Náhled poslední valuace */}
      {recent && (
        <button onClick={() => (onOpen ? onOpen(recent) : onNavigate('dashboard'))} style={recentStrip}>
          <div>
            <div style={{ fontSize: '0.72rem', color: 'var(--text-dim)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Vaše poslední valuace</div>
            <div style={{ color: 'var(--star-white)', fontWeight: 600, marginTop: '0.2rem' }}>
              {recent.company_name || 'Bez názvu'}
              <span style={{ color: 'var(--text-dim)', fontWeight: 400, fontSize: '0.85rem' }}>
                {recent.segment ? ` · ${recent.segment}` : ''}{fmtDate(recent.created_at) ? ` · ${fmtDate(recent.created_at)}` : ''}
              </span>
            </div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontFamily: "'Source Serif 4', Georgia, serif", color: ACCENT, fontSize: '1.25rem' }}>{fmtCZK(recent.result_json?.valuation?.total)}</div>
            <div style={{ fontSize: '0.78rem', color: ACCENT, fontWeight: 600 }}>Otevřít →</div>
          </div>
        </button>
      )}

      {/* JAK TO FUNGUJE */}
      <h2 style={sectionH}>Jak to funguje</h2>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '2rem' }}>
        {STEPS.map(([n, t, d]) => (
          <div key={n} style={stepCard}>
            <div style={stepNum}>{n}</div>
            <div style={{ fontWeight: 700, color: 'var(--star-white)', margin: '0.6rem 0 0.3rem' }}>{t}</div>
            <div style={{ fontSize: '0.85rem', color: 'var(--text-dim)', lineHeight: 1.5 }}>{d}</div>
          </div>
        ))}
      </div>

      {/* CO MŮŽETE ZÍSKAT (tarify) */}
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '0.9rem' }}>
        <h2 style={{ ...sectionH, margin: 0 }}>Tarify</h2>
        <button onClick={() => onNavigate('billing')} style={tariffLink}>Zobrazit detail tarifů →</button>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '0.85rem', marginBottom: '0.75rem', alignItems: 'stretch' }}>
        {TARIFFS.map(t => (
          <button key={t.key} onClick={() => onNavigate('billing')} className="home-tile" style={{
            ...tariffCard,
            border: t.rec ? '2.5px solid var(--purple-glow)' : '1px solid rgba(0,0,0,0.08)',
            boxShadow: t.rec ? '0 10px 26px rgba(91,59,140,0.16)' : '0 1px 3px rgba(0,0,0,0.05)',
            position: 'relative',
          }}>
            {t.rec && <span style={recRibbon}>Nejlepší volba</span>}
            <span style={{ fontWeight: 800, color: 'var(--star-white)', fontSize: '0.95rem', marginTop: t.rec ? '0.4rem' : 0 }}>{t.name}</span>
            <span style={{ fontFamily: "'Source Serif 4', Georgia, serif", color: t.rec ? 'var(--purple-glow)' : 'var(--star-white)', fontSize: '1.25rem', margin: '0.25rem 0' }}>{t.price}</span>
            <span style={{ fontSize: '0.82rem', color: 'var(--text-dim)', lineHeight: 1.45 }}>{t.desc}</span>
          </button>
        ))}
      </div>
      <div style={{ fontSize: '0.85rem', color: 'var(--text-dim)', marginBottom: '2rem' }}>
        Začínáte <strong style={{ color: 'var(--color-success)' }}>základní valuací zdarma</strong>, uvidíte hodnotu firmy.
        Placené tarify pak vysvětlí, proč tolik a jak hodnotu zvýšit.
      </div>

      {/* SEKUNDÁRNÍ AKCE */}
      <h2 style={sectionH}>Co dál v portálu</h2>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '0.85rem' }}>
        {secondary.map(c => (
          <button key={c.key} onClick={c.action} className="home-tile" style={tile}>
            <span style={tileIcon}><Icon name={c.icon} size={20} /></span>
            <span style={{ fontWeight: 700, color: 'var(--star-white)', marginTop: '0.6rem' }}>{c.title}</span>
            <span style={{ fontSize: '0.82rem', color: 'var(--text-dim)', lineHeight: 1.4 }}>{c.desc}</span>
          </button>
        ))}
      </div>

      <style>{`
        .home-tile { transition: transform .14s ease, box-shadow .14s ease, border-color .14s ease; }
        .home-tile:hover { transform: translateY(-2px); box-shadow: 0 8px 20px rgba(91,59,140,0.10); border-color: rgba(91,59,140,0.35); }
      `}</style>
    </div>
  );
}

const hero = {
  background: 'linear-gradient(135deg, var(--purple-deep) 0%, var(--purple-dark) 70%)',
  border: '1px solid rgba(91,59,140,0.18)', borderRadius: 18, padding: '2.25rem 2rem', marginBottom: '1.75rem',
};
const heroCta = {
  background: ACCENT, color: 'white', border: 'none', borderRadius: 10,
  padding: '0.85rem 1.6rem', fontSize: '1rem', fontWeight: 700, cursor: 'pointer',
  boxShadow: '0 4px 16px rgba(91,59,140,0.3)',
};
const sectionH = { fontFamily: "'Source Serif 4', Georgia, serif", color: 'var(--star-white)', fontSize: '1.1rem', margin: '0 0 0.9rem' };
const stepCard = { background: 'var(--purple-dark)', border: '1px solid rgba(0,0,0,0.08)', borderRadius: 14, padding: '1.2rem', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' };
const stepNum = {
  width: 32, height: 32, borderRadius: '50%', background: 'rgba(91,59,140,0.1)', color: ACCENT,
  display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: '0.95rem',
};
const tariffLink = {
  background: 'transparent', border: `1px solid ${ACCENT}`, color: ACCENT, borderRadius: 8,
  padding: '0.5rem 1rem', fontWeight: 600, fontSize: '0.85rem', cursor: 'pointer', whiteSpace: 'nowrap',
};
const tariffCard = {
  textAlign: 'left', cursor: 'pointer', padding: '1.15rem 1.2rem', borderRadius: 14,
  background: 'var(--purple-dark)', display: 'flex', flexDirection: 'column', fontFamily: "'Inter', sans-serif",
};
const recRibbon = {
  position: 'absolute', top: -12, left: '50%', transform: 'translateX(-50%)',
  background: 'var(--btn-bg)', color: '#fff', fontWeight: 800,
  fontSize: '0.72rem', padding: '0.26rem 0.8rem', borderRadius: 999, whiteSpace: 'nowrap',
  boxShadow: '0 4px 12px rgba(91,59,140,0.35)',
};
const tile = {
  textAlign: 'left', cursor: 'pointer', padding: '1.1rem 1.2rem', borderRadius: 14,
  background: 'var(--purple-dark)', border: '1px solid rgba(0,0,0,0.08)', boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
  display: 'flex', flexDirection: 'column', fontFamily: "'Inter', sans-serif",
};
const tileIcon = {
  width: 40, height: 40, borderRadius: 11, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  background: 'rgba(91,59,140,0.09)', color: ACCENT,
};
const recentStrip = {
  width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem',
  padding: '1.1rem 1.25rem', marginBottom: '1.75rem', borderRadius: 14, cursor: 'pointer',
  background: 'var(--purple-dark)', border: '1px solid rgba(0,0,0,0.08)', borderLeft: `3px solid ${ACCENT}`,
  boxShadow: '0 1px 3px rgba(0,0,0,0.05)', textAlign: 'left', fontFamily: "'Inter', sans-serif",
};
